import { getLocaleConfig } from "configs/LangConfig"
import { DEFAULT_LOCALE, LOCALE_KEY, USER_LOCALE_KEY } from "hooks/useLocale"
import { interpolateTranslations } from "hooks/useTranlate/interpolateTranslations"
import { MessageDescriptor, PrimitiveType, createIntl } from "react-intl"

import { Locale } from "@shared/enums/general"
import { isLocalizedText } from "@shared/interfaces/localized"
import { SerializedTranslationEntry, TranslationEntry } from "@shared/translations/translationEntry"

import { getCookie } from "./cookies"
import { isEnum } from "./isEnum"
import { parseEnum } from "./parseEnum"

/**
 *  A function that can be used to translate messages outside our regular
 *  react flow. This is useful in cases when the react as a dependency shouldn't
 *  be provided.
 *
 * @see useTranslate for the regular translation hook
 */
export function translate(descriptor: MessageDescriptor, values?: Record<string, PrimitiveType>) {
  const currentLocale = getCurrentLocale()

  const intl = createIntl({
    ...getLocaleConfig(currentLocale),
    // We override the default on error with a custom function because
    // the createIntl from react-intl is passing a specific function
    // that throws errors in a way that they can't be caught with
    // a try-catch (maybe inside a setTimeout or something like that).
    onError: (e: Error) => {
      throw e
    },
  })

  return intl.formatMessage(descriptor, values)
}

/**
 * Attempts to deserialize a message string as a TranslationEntry and translate it.
 * If the message is not a serialized TranslationEntry, returns the original string as-is.
 */
export function translateMessage(message: string): string {
  try {
    const parsed = JSON.parse(message) as SerializedTranslationEntry
    const entry = TranslationEntry.fromJSON(parsed)

    // fromJSON() guarantees key is either LocalizedText or IntlKey (throws otherwise)
    if (isLocalizedText(entry.key)) {
      const locale = getCurrentLocale()
      return interpolateTranslations(entry.key[locale] as string, entry.data ?? {})
    }

    return translate({ id: entry.key as string }, entry.data)
  } catch {
    // Not a serialized TranslationEntry — return as-is
  }
  return message
}

/**
 * Gets the current locale. First checks the deprecated cookie (still used by E2E tests),
 * then localStorage. This creates a dependency on the hook's storage key — ideally
 * locale handling would live in a non-react mechanism shared by hooks and utility functions.
 */
function getCurrentLocale(): Locale {
  const fallbackLocale = getCookie(LOCALE_KEY)
  return parseEnum(
    window.localStorage.getItem(USER_LOCALE_KEY),
    Locale,
    isEnum(fallbackLocale, Locale) ? fallbackLocale : DEFAULT_LOCALE
  )
}
